// client/src/components/common/PickerTimePresets.tsx
//
// Quick times in the pickers' bottom sheet (ST-00264) and, smaller, in their desktop popup
// (ST-00294): a wrapping row of chips the field's
// page chooses (Plan & Dispatch's planned start: Now, +30 min, +1 h, +2 h, Tomorrow 06:00), each
// printing the time it picks. They are worked out from the clock while the sheet is open (it
// re-reads the clock every 15 s) and only the ones inside the picker's limits are shown. The chip
// matching the pending value is pressed.

import { useEffect, useState } from 'react';
import {
  formatHm,
  resolveTimePresets,
  sameMinute,
  sameTime,
  timeOf,
  type DayLimits,
  type TimePreset,
} from './pickerLogic';

const CLOCK_TICK_MS = 15_000;

/** The current time, re-read every 15 s while mounted. */
function useClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), CLOCK_TICK_MS);
    return () => clearInterval(id);
  }, []);
  return now;
}

export interface PickerTimePresetsProps {
  presets: readonly TimePreset[];
  /** The pending value (pressed chip), or null. */
  value: Date | null;
  onPick: (at: Date) => void;
  /** The picker's days (and 'past' / 'future' window); chips outside them are left out. */
  limits?: DayLimits;
  /** A time-only field: a chip is pressed when its time of day matches. */
  timeOnly?: boolean;
  label?: string;
}

export default function PickerTimePresets({
  presets,
  value,
  onPick,
  limits,
  timeOnly = false,
  label = 'Quick times',
}: PickerTimePresetsProps) {
  const now = useClock();
  const items = resolveTimePresets(presets, limits, now);
  if (!items.length) return null;
  const pressed = (at: Date) =>
    !!value && (timeOnly ? sameTime(timeOf(value), timeOf(at)) : sameMinute(value, at));
  return (
    <div className="picker-chips picker-chips--wrap" role="group" aria-label={label}>
      {items.map((p) => {
        // A time-only field has no day to name.
        const detail = timeOnly && p.detail ? formatHm(timeOf(p.at)) : p.detail;
        return (
          <button
            key={p.id}
            type="button"
            className="picker-chip picker-chip--time"
            data-preset={p.id}
            aria-pressed={pressed(p.at)}
            onClick={() => onPick(p.at)}
          >
            {p.label}
            {detail && (
              <>
                {' '}
                <span className="picker-chip__detail">{detail}</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
